import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Sentry } from "@/lib/sentry";
import { AuthProvider } from "@/hooks/use-auth";
import { SubscriptionProvider } from "@/hooks/use-subscription";
import { BuildCreditsProvider } from "@/hooks/use-build-credits";
import { ThemeProvider } from "next-themes";
import { UpgradePromptOverlay } from "@/components/upgrade-prompt-overlay";
import { PaymentFailedBanner } from "@/components/payment-failed-banner";
import { RouteMeta } from "@/components/route-meta";
const Index = lazy(() => import("./pages/Index"));
const Chat = lazy(() => import("./pages/Chat"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const Subscription = lazy(() => import("./pages/Subscription"));
const Settings = lazy(() => import("./pages/Settings"));
const FAQ = lazy(() => import("./pages/FAQ"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Consultation = lazy(() => import("./pages/Consultation"));
const NotFound = lazy(() => import("./pages/NotFound"));
const VerifySuccess = lazy(() => import("./pages/VerifySuccess"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const For = lazy(() => import("./pages/For"));
const ForVertical = lazy(() => import("./pages/ForVertical"));
const SharedBuild = lazy(() => import("./pages/SharedBuild"));
const Gallery = lazy(() => import("./pages/Gallery"));
const PersonasPage = lazy(() => import("./pages/PersonasPage"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const TeamBuildsPage = lazy(() => import("./pages/TeamBuildsPage"));
const TeamAcceptInvitePage = lazy(() => import("./pages/TeamAcceptInvitePage"));
const ExtensionAuthorize = lazy(() => import("./pages/ExtensionAuthorize"));
const EmailPrefs = lazy(() => import("./pages/EmailPrefs"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});
const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-48 rounded-md bg-muted animate-pulse" />
      <div className="h-4 w-32 rounded-md bg-muted animate-pulse" />
    </div>
  </div>
);

const ErrorFallback = () => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-6 text-center">
    <h1 className="text-2xl font-semibold">Something went wrong.</h1>
    <p className="text-muted-foreground">
      We've been notified and are looking into it. Try reloading the page.
    </p>
    <button
      type="button"
      onClick={() => window.location.reload()}
      className="mt-2 px-4 py-2 rounded-md bg-primary text-primary-foreground"
    >
      Reload
    </button>
  </div>
);

const App = () => (
  <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} disableTransitionOnChange>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SubscriptionProvider>
          <BuildCreditsProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <RouteMeta />
                <UpgradePromptOverlay />
                <PaymentFailedBanner />
                <Suspense fallback={<RouteFallback />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/chat" element={<Chat />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/subscription" element={<Subscription />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/consultation" element={<Consultation />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/verify-success" element={<VerifySuccess />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/for" element={<For />} />
                    <Route path="/for/:vertical" element={<ForVertical />} />
                    <Route path="/p/:shortId" element={<SharedBuild />} />
                    <Route path="/gallery" element={<Gallery />} />
                    <Route path="/settings/personas" element={<PersonasPage />} />
                    <Route path="/team" element={<TeamPage />} />
                    <Route path="/team/builds" element={<TeamBuildsPage />} />
                    <Route path="/team/accept/:token" element={<TeamAcceptInvitePage />} />
                    <Route path="/extension/authorize" element={<ExtensionAuthorize />} />
                    <Route path="/email-prefs" element={<EmailPrefs />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </TooltipProvider>
          </BuildCreditsProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ThemeProvider>
  </Sentry.ErrorBoundary>
);

export default App;
